import { ReactNode, useMemo } from "react";
import { GrClose } from "react-icons/gr";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Box } from "../../../components/Box";
import {
  Breadcrumb,
  StaticBreadcrumb,
  Crumb,
  BreadcrumbDivider,
} from "../../../components/Breadcrumbs";
import { Flex } from "../../../components/Flex";
import { IconButton } from "../../../components/IconButton";
import Topbar from "../../../components/Topbar";
import { ProgressBar } from "../../../components/ProgressBar";

type Step = "entityDetails" | "applicationType" | "addContact" | "sendInvites";

const allSteps: Step[] = [
  "entityDetails",
  "applicationType",
  "addContact",
  "sendInvites",
];

const titles: Record<Step, string> = {
  entityDetails: "Details",
  applicationType: "Application Type",
  addContact: "Add Contact",
  sendInvites: "Send Invites",
};

export const NewProfileApplicationFrame = ({
  children,
  step,
}: {
  children: ReactNode;
  step: Step;
}) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const { profile } = useOutletContext<any>();
  const isEntity = profile?._type === "entity";

  // ✅ 3 steps for Person (skip confirm email / addContact)
  const steps = useMemo<Step[]>(
    () => (isEntity ? allSteps : allSteps.filter((s) => s !== "addContact")),
    [isEntity]
  );

  const currentStepIndex = steps.indexOf(step);
  const progress =
    currentStepIndex >= 0 ? ((currentStepIndex + 1) * 100) / steps.length : 0;

  const crumbs = useMemo(() => {
    const entityOrIndividualLabel = isEntity
      ? "Entity Details"
      : "Individual Details";

    return steps.map((s, idx) => {
      let label = titles[s];

      if (s === "entityDetails") label = entityOrIndividualLabel;
      // NOTE: "addContact" isn't in steps for Person, so no need to rename it.

      return {
        key: idx + 1,
        isActive: s === step,
        title: `${String(idx + 1).padStart(2, "0")} ${label}`,
      };
    });
  }, [isEntity, step, steps]);

  return (
    <div>
      <Topbar
        header={
          <Flex justifyContent="space-between" width="100%">
            <Breadcrumb>
              <Breadcrumb.Section linkTo={`/profile/${profileId}`}>
                Profiles
              </Breadcrumb.Section>
              <Breadcrumb.Section>Create a new application</Breadcrumb.Section>
            </Breadcrumb>

            <Flex>
              <IconButton
                Type={GrClose}
                onClick={() => navigate(`/profile/${profileId}`)}
                color="gray.40"
                ml={4}
                mr={3}
              />
            </Flex>
          </Flex>
        }
      />

      <Box height="calc(100vh - 155px)" overflowY="auto">
        <Box>
          <ProgressBar progress={progress} />
          <Box padding={2} marginLeft={2}>
            <StaticBreadcrumb>
              {crumbs.map(({ isActive, title, key }, index) => (
                <Crumb
                  key={key}
                  isActive={isActive}
                  iconDivider={index < crumbs.length - 1 && <BreadcrumbDivider />}
                >
                  {title}
                </Crumb>
              ))}
            </StaticBreadcrumb>
          </Box>
        </Box>

        <Flex position="relative" justifyContent="center" alignItems="center">
          <Box width="650px" maxWidth="calc(100vw - 48px)">
            {children}
          </Box>
        </Flex>
      </Box>
    </div>
  );
};
